import { PATH } from '@/constants'
import { formatTitleForUrl, getDataFromUrlExtended } from '@/helpers'
import { CategoryId, CategoryTitle } from './categories'

type EpgPathSegments = {
  regionId?: string | number | null
  regionTitle?: string
  categoryId?: CategoryId | number | null
  categoryTitle?: string
  channelId?: string | number | null
  channelTitle?: string
}

/**
 * Joins path segments, ensuring no double slashes
 */
function joinPath(...segments: string[]): string {
  return '/' + segments.filter(Boolean).join('/')
}

function resolveCategoryId(
  id: number | string | null | undefined
): CategoryId | number | null {
  if (id == null) return null
  if (id === CategoryId.All || id === CategoryId.Favorite) return id
  if (typeof id === 'number') return id
  return null
}

/**
 * Builds an EPG URL path with the given segments, maintaining their semantic roles
 * and formatting titles in SEO-friendly way
 */
export function updateSegments(segments: EpgPathSegments): string {
  const {
    regionId,
    regionTitle,
    categoryId,
    categoryTitle,
    channelId,
    channelTitle,
  } = segments
  const parts = [
    PATH.LIVE.replace(/^\//, ''),
    PATH.CATEGORIES.replace(/^\//, ''),
  ]

  // We always add segments in order to maintain their semantic roles
  // If a segment is null/undefined, we skip it but still maintain the order of others
  if (regionId != null) {
    const segment = regionTitle
      ? `${formatTitleForUrl(regionTitle)}${regionId}`
      : String(regionId)
    parts.push(segment)

    // If categoryId is explicitly null, don't add category or channel
    if (categoryId === null) {
      return joinPath(...parts)
    }

    // If channel is provided but no category, default to 'All' category
    const effectiveCategoryId =
      channelId != null && categoryId == null ? CategoryId.All : categoryId

    // Add category if we have one (either explicit or defaulted to All)
    if (effectiveCategoryId != null) {
      if (
        effectiveCategoryId === CategoryId.All ||
        effectiveCategoryId === CategoryId.Favorite
      ) {
        parts.push(effectiveCategoryId)
      } else {
        const segment = categoryTitle
          ? `${formatTitleForUrl(categoryTitle)}${effectiveCategoryId}`
          : String(effectiveCategoryId)
        parts.push(segment)
      }

      // Only add channel if we have both region and category
      if (channelId != null) {
        const segment = channelTitle
          ? `${formatTitleForUrl(channelTitle)}${channelId}`
          : String(channelId)
        parts.push(segment)
      }
    }
  }

  return joinPath(...parts)
}

/**
 * Parses an EPG URL path into its segments
 */
export function parseEpgPath(pathname: string): EpgPathSegments {
  if (!pathname?.includes(PATH.CATEGORIES)) {
    return {}
  }

  const segments = pathname.split('/').filter(Boolean)
  const categoriesIndex = segments.findIndex((s) => s === 'categories')

  if (categoriesIndex === -1) {
    return {}
  }

  // Get segments after 'categories'
  const pathSegments = segments.slice(categoriesIndex + 1)

  // Extract segments into named constants
  const regionSegment = pathSegments[0]
  const categorySegment = pathSegments[1]
  const channelSegment = pathSegments[2]

  // Parse each segment to extract both ID and title
  const regionData = regionSegment
    ? getDataFromUrlExtended(regionSegment)
    : null

  // Handle special categories (all, my-list)
  let categoryData = null
  if (regionSegment && categorySegment) {
    if (
      categorySegment === CategoryId.All ||
      categorySegment === CategoryId.Favorite
    ) {
      categoryData = {
        id:
          categorySegment === CategoryId.All
            ? CategoryId.All
            : CategoryId.Favorite,
        title:
          categorySegment === CategoryId.All
            ? CategoryTitle.All
            : CategoryTitle.Favorite,
      }
    } else {
      categoryData = getDataFromUrlExtended(categorySegment)
    }
  }

  const channelData =
    regionSegment && categorySegment && channelSegment
      ? getDataFromUrlExtended(channelSegment)
      : null

  return {
    regionId: regionData?.id ?? null,
    regionTitle: regionData?.title ?? '',
    // Only set category if we have a region
    categoryId:
      regionData && categoryData
        ? resolveCategoryId(categoryData.id)
        : undefined,
    categoryTitle: regionData ? categoryData?.title : '',
    // Only set channel if we have both region and category
    channelId: regionData && categoryData ? channelData?.id : null,
    channelTitle: regionData && categoryData ? channelData?.title : '',
  }
}

/**
 * Updates segments in an existing EPG path while preserving their semantic roles
 * and formatting titles in URL-friendly way
 */
export function updateEpgPath(
  pathname: string | null,
  updates: EpgPathSegments
): string {
  // If pathname is empty or invalid, build a new path
  if (!pathname?.includes(PATH.CATEGORIES)) {
    return updateSegments(updates)
  }

  const current = parseEpgPath(pathname)

  // Build the updated path while maintaining semantic roles
  const updatedSegments = {
    ...current,
    ...updates,
  }

  return updateSegments({
    regionId: updatedSegments.regionId,
    regionTitle: updatedSegments.regionTitle,
    categoryId: updatedSegments.categoryId as CategoryId | number,
    categoryTitle: updatedSegments.categoryTitle,
    channelId: updatedSegments.channelId,
    channelTitle: updatedSegments.channelTitle,
  })
}

type EpgUrlParams = {
  regionId: string | number | null | undefined
  regionTitle?: string
  categoryId?: CategoryId | number | null
  categoryTitle?: string
  channelId?: string | number | null
  channelTitle?: string
}

/**
 * Builds a display-friendly EPG URL path that includes both IDs and titles
 * For special categories (all, my-list) uses predefined values
 * For regular categories includes both title and ID
 */
export function buildEpgUrl(params: EpgUrlParams): string {
  const {
    regionId,
    regionTitle,
    categoryId,
    categoryTitle,
    channelId,
    channelTitle,
  } = params

  // Format region segment
  const regionSegment = regionId
    ? `${formatTitleForUrl(regionTitle)}${regionId}`
    : 'null'

  // Format category segment based on special cases
  let categorySegment = ''
  if (categoryId === CategoryId.All || categoryId === CategoryId.Favorite) {
    categorySegment = categoryId
  } else if (categoryId) {
    categorySegment = `${formatTitleForUrl(categoryTitle)}${categoryId}`
  }

  // Build base path
  const parts = [
    PATH.LIVE.replace(/^\//, ''),
    PATH.CATEGORIES.replace(/^\//, ''),
    regionSegment,
    categorySegment,
  ]

  // Add channel if provided
  if (channelId != null) {
    parts.push(`${formatTitleForUrl(channelTitle)}${channelId}`)
  }

  return joinPath(...parts)
}
